import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

const links = [
  { label: "The Sync", href: "#sync", testid: "nav-link-sync" },
  { label: "The Standard", href: "#standard", testid: "nav-link-standard" },
  { label: "Zones", href: "#zones", testid: "nav-link-zones" },
  { label: "Drive With Us", href: "#drive", testid: "nav-link-drive" },
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
];

const signIn = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const AuthControls = ({ me, onSignOut }) => {
  if (!me)
    return (
      <button
        type="button"
        data-testid="google-signin-btn"
        onClick={signIn}
        className="hidden font-manrope text-sm font-semibold text-forest/70 transition-colors duration-300 hover:text-forest sm:block"
      >
        Sign in
      </button>
    );
  return (
    <div data-testid="nav-user" className="hidden items-center gap-2 sm:flex">
      {me.picture && (
        <img
          src={me.picture}
          alt={me.name || me.email}
          referrerPolicy="no-referrer"
          className="h-8 w-8 rounded-full border border-forest/15"
        />
      )}
      <span className="max-w-[120px] truncate font-manrope text-sm font-semibold text-forest/80">
        {(me.name || me.email).split(" ")[0]}
      </span>
      <button
        type="button"
        title="Sign out"
        data-testid="google-signout-btn"
        onClick={onSignOut}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-forest/15 text-forest/60 transition-colors hover:border-forest/40 hover:text-forest"
      >
        <LogOut className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const MobileMenu = ({ open, me, go, onSignOut }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="mobile-menu"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden md:hidden"
      >
        <div className="flex flex-col px-6 pb-6 pt-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`mobile-${l.testid}`}
              className="border-b border-forest/10 py-4 font-syne text-lg font-bold text-forest transition-colors last:border-0 hover:text-fresh"
            >
              {l.label}
            </a>
          ))}
          {me ? (
            <button
              type="button"
              data-testid="mobile-google-signout-btn"
              onClick={onSignOut}
              className="py-4 text-left font-syne text-lg font-bold text-forest/70 transition-colors hover:text-fresh"
            >
              Sign out ({(me.name || me.email).split(" ")[0]})
            </button>
          ) : (
            <button
              type="button"
              data-testid="mobile-google-signin-btn"
              onClick={signIn}
              className="py-4 text-left font-syne text-lg font-bold text-forest transition-colors hover:text-fresh"
            >
              Sign in with Google
            </button>
          )}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

const DesktopLinks = ({ go }) => (
  <div className="hidden items-center gap-10 md:flex">
    {links.map((l) => (
      <a
        key={l.href}
        href={l.href}
        onClick={(e) => go(e, l.href)}
        data-testid={l.testid}
        className="font-manrope text-sm font-semibold text-forest/70 transition-colors duration-300 hover:text-forest"
      >
        {l.label}
      </a>
    ))}
  </div>
);

const headerClass = (open, scrolled) => {
  if (open) return "bg-bone shadow-[0_1px_0_0_rgba(4,47,30,0.08)]";
  if (scrolled) return "bg-bone/90 shadow-[0_1px_0_0_rgba(4,47,30,0.08)] backdrop-blur-xl";
  return "bg-transparent";
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setMe(r.data))
      .catch(() => {});
  }, []);

  const signOut = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setMe(null);
    setOpen(false);
  };

  const go = (e, href) => {
    e.preventDefault();
    const wasOpen = open;
    setOpen(false);
    const scroll = () => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    if (wasOpen) setTimeout(scroll, 420);
    else scroll();
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${headerClass(open, scrolled)}`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          data-testid="nav-logo"
          className="font-syne text-2xl font-extrabold tracking-tight text-forest"
        >
          Freshly<span className="text-fresh">.</span>
        </a>
        <DesktopLinks go={go} />
        <div className="flex items-center gap-3">
          <AuthControls me={me} onSignOut={signOut} />
          <motion.a
            href="#top"
            onClick={(e) => go(e, "#top")}
            data-testid="nav-cta-button"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            className="rounded-full bg-forest px-6 py-2.5 font-manrope text-sm font-bold text-bone transition-colors duration-300 hover:bg-fresh"
          >
            Get the App
          </motion.a>
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-forest/15 text-forest md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      <MobileMenu open={open} me={me} go={go} onSignOut={signOut} />
    </motion.header>
  );
};
